import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import COLORS from "../../../../assets/Colors";
import { InfoLabel } from "../../../internal/PartyDetails";
import theme from "../../../../assets/theme";
import DrawerModal from "./ManagementModal";
import ManageEpeOperatorModal from "./ManageEpeOperatorModal";
import StartHearingSessionModal from "./StartHearingSessionModal";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import ReplayIcon from "@material-ui/icons/Replay";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import { PrimaryCTAButton } from "../../Buttons";
import useSettings from "../../../../hooks/useSettings";
import useEpeAccess from "../../../../hooks/useEpeAccess";
import { getFileName, listJoin } from "../../../../helpers/functions";

const EASE_OUT_EXPO = "cubic-bezier(0.16, 1, 0.3, 1)";
const EASE_SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const motionSafe = (animationCss) => css`
  ${animationCss}
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// "New hearing session started at 14:05" (24-hour, local time)
const startedAtText = (at) =>
  `New hearing session started at ${String(at.getHours()).padStart(2, "0")}:${String(
    at.getMinutes(),
  ).padStart(2, "0")}`;

const TribunalSection = ({
  caseManager,
  title = "Case Manager",
  addMediator,
  disabled = false,
  showRemove = false,
  removeMediator = () => {},
  adhocPending = false,
  mediator,
  caseDetails,
  titleTag,
  onTogglePresiding,
  makePresidingArbitrator = () => {},
  pushCaseToArbitrationAlert = () => {},
  openPayments = () => {},
  viewOnly,
}) => {
  const [modalData, setModalData] = useState(null);
  const [manageEpeOpen, setManageEpeOpen] = useState(false);
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  // { caseId, at }: the page can move to another case without remounting, so the line is kept per case.
  const [sessionStarted, setSessionStarted] = useState(null);
  const { setting } = useSettings();
  const { access: epeAccess } = useEpeAccess();
  const sessionStartedAt =
    sessionStarted && sessionStarted.caseId === caseDetails?.id
      ? sessionStarted.at
      : null;

  // EPE (JustCue) is not used for negotiation cases; only the case's case managers manage it.
  const epeEnabled =
    caseDetails?.resolutionKind !== "negotiation" && !!epeAccess?.enabled;
  const canManageEpe =
    epeEnabled && !!epeAccess?.canManageOperators && !viewOnly;
  const epeOperatorNames = (epeAccess?.operators || []).map(
    (o) => o.name || o.id,
  );

  const mediators = caseDetails?.requestedMediators || [];
  const presidingArbitrator =
    mediators.find((m) => m.presidingArbitrator === "yes") || {};
  const associateArbitrators = mediators.filter(
    (m) => m.id !== presidingArbitrator.id,
  );
  const isMediation = caseDetails?.resolutionKind === "mediation";

  const primaryCaseManager = caseDetails?.caseManager || {};
  const associateCaseManagers = caseDetails?.caseManagerIds || [];

  const getInitials = (name) => {
    if (!name) return "NA";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const openModal = (person, type) => {
    setModalData({ person, type });
  };

  const data = {
    tribunal: {
      presiding: {
        ...presidingArbitrator,
        name: presidingArbitrator.name || "",
        role: isMediation ? "Presiding Mediator" : "Presiding Arbitrator",
        appointedDate: presidingArbitrator.date
          ? new Date(
              presidingArbitrator.date
                .split(" ")[0]
                .split("/")
                .reverse()
                .join("-"),
            ).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "N/A",
      },
      associates: associateArbitrators.slice(0, 3).map((m, i) => ({
        ...m,
        initials: getInitials(m.name),
        color: ["#2c3e50", "#8e44ad", "#e74c3c", "#f39c12"][i % 4],
      })),
      associatesCount: associateArbitrators.length,
      associatesData: associateArbitrators,
    },
    caseManagement: {
      lead: {
        initials: getInitials(primaryCaseManager.name),
        name: primaryCaseManager.name || "Lead Case Manager",
        email: primaryCaseManager?.email,
        mobile: primaryCaseManager?.mobile,
        role: "Lead Case Manager",
        isYou: true,
      },
      associates: associateCaseManagers.slice(0, 3).map((cm, i) => ({
        ...cm,
        initials: getInitials(cm.name),
        color: ["#2c3e50", "#8e44ad", "#e74c3c", "#f39c12"][i % 4],
      })),
      associatesCount: associateCaseManagers.length,
      associatesData: associateCaseManagers,
    },
    seat: caseDetails?.seat || "Chennai",
    rules:
      setting?.rulesDocumentUrl ||
      "Arbitration & Conciliation Act, 1996 (as amended)",
  };

  return (
    <SectionWrapper>
      <MainContainer>
        <LeftPanel>
          <Card>
            <InfoLabel
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {caseDetails?.resolutionKind === "mediation"
                ? "Mediator(s)"
                : "Arbitrator(s)"}

              {(caseDetails?.resolutionKind === "arbitration" ||
                (caseDetails?.resolutionKind === "mediation" &&
                  !caseDetails?.requestedMediators?.length)) &&
                !viewOnly && (
                  <AddButton onClick={addMediator}>
                    <AddIconWrap>
                      <AddIcon style={{ fontSize: 18 }} />
                    </AddIconWrap>
                    {caseDetails?.resolutionKind === "mediation"
                      ? "Add Mediator"
                      : "Add Arbitrator"}
                  </AddButton>
                )}
            </InfoLabel>

            <CaseManagementRow>
              {data.tribunal.presiding.name && (
                <LeadManager>
                  {data.tribunal.presiding.avatarUrl ? (
                    <CaseManagerProfile
                      src={data.tribunal.presiding.avatarUrl}
                    />
                  ) : (
                    <InitialsCircle color="#00a884">
                      {getInitials(data.tribunal.presiding.name)}
                    </InitialsCircle>
                  )}
                  <div style={{ flex: 1 }}>
                    <Name
                      onClick={() =>
                        openModal(
                          data.tribunal.presiding,
                          isMediation ? "Mediator" : "Arbitrator",
                        )
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {data.tribunal.presiding.name}
                    </Name>
                    <Designation>
                      {isMediation
                        ? "Presiding Mediator"
                        : "Presiding Arbitrator"}{" "}
                      · Appointed {data.tribunal.presiding.appointedDate}
                    </Designation>
                  </div>
                </LeadManager>
              )}

              {data.tribunal.associatesCount > 0 && (
                <AssociatesSection>
                  <AvatarGroup>
                    {data.tribunal.associates.map((assoc, i) =>
                      assoc?.avatarUrl ? (
                        <CaseManagerProfile
                          key={i}
                          src={assoc.avatarUrl}
                          $index={i}
                        />
                      ) : (
                        <MiniAvatar key={i} color={assoc.color} $index={i}>
                          {assoc.initials}
                        </MiniAvatar>
                      ),
                    )}
                  </AvatarGroup>
                  <AssociatesInfo>
                    <AssociateCount>
                      {data.tribunal.associatesCount} Associate{" "}
                      {isMediation ? "Mediator" : "Arbitrator"}
                      {data.tribunal.associatesCount > 1 ? "s" : ""}
                    </AssociateCount>
                    <AssociateNames>
                      {data.tribunal.associatesData.map((cm, index) => (
                        <React.Fragment key={cm.id || index}>
                          <AssociateLink
                            onClick={() =>
                              openModal(
                                cm,
                                isMediation ? "Mediator" : "Arbitrator",
                              )
                            }
                          >
                            {cm.name}
                          </AssociateLink>
                          {index !== data.tribunal.associatesData.length - 1 &&
                            " · "}
                        </React.Fragment>
                      ))}
                    </AssociateNames>
                  </AssociatesInfo>
                </AssociatesSection>
              )}
            </CaseManagementRow>

            <Divider />

            <CaseManagementHeader>
              <InfoLabel style={{ margin: 0 }}>CASE MANAGEMENT</InfoLabel>
              {canManageEpe && (
                <EpeActions>
                  <EpeActionButton
                    type="button"
                    aria-haspopup="dialog"
                    onClick={() => setNewSessionOpen(true)}
                  >
                    <ReplayIcon style={{ fontSize: 16 }} />
                    Start new hearing session
                  </EpeActionButton>
                  <EpeActionButton
                    type="button"
                    aria-haspopup="dialog"
                    onClick={() => setManageEpeOpen(true)}
                  >
                    <SupervisorAccountIcon style={{ fontSize: 16 }} />
                    Manage EPE operator
                  </EpeActionButton>
                </EpeActions>
              )}
            </CaseManagementHeader>

            {canManageEpe && (
              <SessionStartedLine role="status">
                {sessionStartedAt && (
                  <>
                    <CheckIcon
                      style={{ fontSize: 16, color: COLORS.BTN_GREEN }}
                    />
                    {startedAtText(sessionStartedAt)}
                  </>
                )}
              </SessionStartedLine>
            )}

            <CaseManagementRow>
              <LeadManager>
                {data.caseManagement.lead.avatarUrl ? (
                  <CaseManagerProfile
                    src={data.caseManagement.lead.avatarUrl}
                  />
                ) : (
                  <InitialsCircle color="#00a884">
                    {data.caseManagement.lead.initials}
                  </InitialsCircle>
                )}
                <div>
                  <Name
                    onClick={() =>
                      openModal(data.caseManagement.lead, "Case Manager")
                    }
                    style={{ cursor: "pointer" }}
                  >
                    {data.caseManagement.lead.name}
                  </Name>
                  <Designation>{data.caseManagement.lead.role}</Designation>
                </div>
              </LeadManager>

              {data.caseManagement.associatesCount > 0 && (
                <AssociatesSection>
                  <AvatarGroup>
                    {data.caseManagement.associates.map((assoc, i) =>
                      assoc?.avatarUrl ? (
                        <CaseManagerProfile
                          key={i}
                          src={assoc.avatarUrl}
                          $index={i}
                        />
                      ) : (
                        <MiniAvatar key={i} color={assoc.color} $index={i}>
                          {assoc.initials}
                        </MiniAvatar>
                      ),
                    )}
                  </AvatarGroup>
                  <AssociatesInfo>
                    <AssociateCount>
                      {data.caseManagement.associatesCount} Associate Case
                      Managers
                    </AssociateCount>
                    <AssociateNames>
                      {data.caseManagement.associatesData.map((cm, index) => (
                        <React.Fragment key={cm.id || index}>
                          <AssociateLink
                            onClick={() =>
                              openModal(cm, "Associate Case Managers")
                            }
                          >
                            {cm.name}
                          </AssociateLink>
                          {index !==
                            data.caseManagement.associatesData.length - 1 &&
                            " · "}
                        </React.Fragment>
                      ))}
                    </AssociateNames>
                  </AssociatesInfo>
                </AssociatesSection>
              )}
            </CaseManagementRow>

            {epeEnabled && (
              <EpeOperatorLine>
                <EpeOperatorLabel>EPE operator</EpeOperatorLabel>
                <EpeOperatorNames aria-live="polite">
                  {epeOperatorNames.length
                    ? listJoin(epeOperatorNames)
                    : "Not assigned yet"}
                </EpeOperatorNames>
              </EpeOperatorLine>
            )}
          </Card>
        </LeftPanel>

        <RightPanel>
          <Card>
            <InfoLabel>RULES</InfoLabel>
            <HyperLink onClick={() => window.open(data.rules)}>
              {getFileName(data.rules)}
            </HyperLink>
          </Card>
          <Card>
            <PrimaryCTAButton
              style={{ marginTop: 8, width: "100%" }}
              onClick={openPayments}
            >
              Payments
            </PrimaryCTAButton>
            {caseDetails?.resolutionKind === "mediation" && !viewOnly && (
              <PrimaryCTAButton
                style={{ marginTop: 8, width: "100%" }}
                onClick={() => pushCaseToArbitrationAlert(caseDetails?.id)}
              >
                Move to Arbitration
              </PrimaryCTAButton>
            )}
          </Card>
        </RightPanel>
      </MainContainer>

      {modalData && (
        <DrawerModal
          modal={!!modalData}
          setModal={() => setModalData(null)}
          id={modalData.person?.id}
          type={modalData.type}
          details={modalData.person}
          {...{
            caseDetails,
            removeMediator,
            makePresidingArbitrator,
            addMediator,
          }}
        />
      )}

      {manageEpeOpen && (
        <ManageEpeOperatorModal
          caseId={caseDetails?.id}
          onClose={() => setManageEpeOpen(false)}
        />
      )}

      {newSessionOpen && (
        <StartHearingSessionModal
          caseId={caseDetails?.id}
          onClose={() => setNewSessionOpen(false)}
          onStarted={(startedAt) => {
            const at = new Date(startedAt);
            setSessionStarted({
              caseId: caseDetails?.id,
              at: isNaN(at.getTime()) ? new Date() : at,
            });
          }}
        />
      )}
    </SectionWrapper>
  );
};

export default TribunalSection;

const SectionWrapper = styled.div`
  padding: 0px 0px 16px;
  background: #f8fafc;
  border-radius: 12px;
`;

const MainContainer = styled.div`
  display: flex;
  gap: 12px;

  & .add_circle {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    background-color: rgba(0, 131, 140, 0.13);
    color: ${COLORS.BTN_GREEN};
    font-family: ${theme?.fonts?.primaryFontSemiBold};
  }

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 2;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Card = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  opacity: 0;
  ${motionSafe(css`
    animation: ${fadeInUp} 0.5s ${EASE_OUT_EXPO} forwards;
  `)}
  transition:
    box-shadow 0.3s ${EASE_OUT_EXPO},
    border-color 0.3s ${EASE_OUT_EXPO};

  &:hover {
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.05);
    border-color: #d6dbe8;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 20px 0;
`;

const CaseManagementRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LeadManager = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 12px;
  border-right: 1px solid #e2e8f0;
  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 12px;
  }
`;

const AssociatesSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 12px;
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 0;
    padding-top: 8px;
  }
`;

const InitialsCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => props.color || "#00a884"};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  transition: transform 0.3s ${EASE_SPRING};

  ${LeadManager}:hover & {
    transform: scale(1.06);
  }
`;

const CaseManagerProfile = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid white;
  flex-shrink: 0;
  transition: transform 0.25s ${EASE_SPRING};

  ${AssociatesSection}:hover & {
    transform: translateX(${(props) => (props.$index || 0) * 3}px);
  }
`;

const Name = styled.div`
  font-size: 14.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  cursor: pointer;
  transition: color 0.2s ${EASE_OUT_EXPO};
  &:hover {
    color: ${COLORS.BTN_GREEN};
    text-decoration: underline;
  }
`;

const Designation = styled.div`
  font-size: 12.5px;
  color: ${COLORS.INPUT_LABEL};
  margin-top: 2px;
`;

const AvatarGroup = styled.div`
  display: flex;
  gap: -8px;
  align-items: center;
  margin-top: 4px;
`;

const MiniAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => props.color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  margin-left: -8px;
  border: 3px solid white;
  flex-shrink: 0;
  transition: transform 0.25s ${EASE_SPRING};

  &:first-child {
    margin-left: 0;
  }

  ${AssociatesSection}:hover & {
    transform: translateX(${(props) => (props.$index || 0) * 3}px);
  }
`;

const AssociatesInfo = styled.div``;

const AssociateCount = styled.div`
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
`;

export const AssociateNames = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
`;

export const AssociateLink = styled.span`
  cursor: pointer;
  color: #64748b;
  transition: color 0.2s ${EASE_OUT_EXPO};
  &:hover {
    color: ${COLORS.BTN_GREEN};
    text-decoration: underline;
  }
`;

const AddIconWrap = styled.span`
  display: inline-flex;
  transition: transform 0.3s ${EASE_SPRING};
`;

export const AddButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border-radius: 999px;
  color: ${COLORS.BTN_GREEN};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  white-space: nowrap;
  padding: 4px 10px;
  transition: background 0.25s ${EASE_OUT_EXPO};

  &:hover {
    background: rgba(0, 131, 140, 0.1);
  }

  &:hover ${AddIconWrap} {
    transform: rotate(90deg);
  }
`;

const HyperLink = styled.p`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.BTN_GREEN};
  font-size: 15px;
  margin: 0;
  cursor: pointer;
  word-break: break-word;
  transition: opacity 0.2s ${EASE_OUT_EXPO};

  &:hover {
    text-decoration: underline;
    opacity: 0.8;
  }
`;

// ── EPE (JustCue): case management header, new-session line and operator line

const CaseManagementHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 0 8px;
`;

const EpeActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const EpeActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${COLORS.BTN_GREEN};
  border-radius: 8px;
  background: white;
  color: ${COLORS.BTN_GREEN};
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.25s ${EASE_OUT_EXPO};

  &:hover {
    background: rgba(0, 131, 140, 0.1);
  }
`;

const SessionStartedLine = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};

  &:not(:empty) {
    margin: 0 0 12px;
  }
`;

const EpeOperatorLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 16px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #eef1f6;
  background: #f8fafc;
  font-size: 13px;
  line-height: 1.5;
`;

const EpeOperatorLabel = styled.span`
  flex-shrink: 0;
  font-family: ${theme.fonts.primaryFontBold};
  color: ${COLORS.COLOR_DARK};
`;

const EpeOperatorNames = styled.span`
  min-width: 0;
  color: #64748b;
  word-break: break-word;
`;
